import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Box, Stepper, Step, StepLabel, Button, Typography, TextField, Alert, 
    InputAdornment, CircularProgress, Tooltip, FormControlLabel, Checkbox, Link
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import GenericForm from './GenericForm.js';
import { userFormFields as formFieldsConfig } from '../forms/userFormFields.js';
import * as courseService from '../services/courseService';

const steps = ['Dati Account', 'Dati Anagrafici', 'Competenze e Carriera'];

export const StudentForm = ({ onRegisterSuccess }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    password: '',
    categories: [],
    courses: [],
    accessCode: '',
    acceptedTerms: false,
  });
  const [error, setError] = useState('');
  const [dynamicFormFields, setDynamicFormFields] = useState(formFieldsConfig);

  const [isVerifying, setIsVerifying] = useState(false);
  const [codeIsValid, setCodeIsValid] = useState(null);
  const [codeError, setCodeError] = useState('');

  // Stato per l'animazione di caricamento durante l'invio finale
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get('/api/categories'),
      courseService.getAllCourses()
    ]).then(([categoriesRes, coursesData]) => {
      const updatedFields = formFieldsConfig.map(field => {
        if (field.name === 'categories') {
          return { ...field, options: categoriesRes.data };
        }
        if (field.name === 'courses') {
          return { ...field, options: coursesData };
        }
        return field;
      });
      setDynamicFormFields(updatedFields);
    }).catch(err => {
      console.error("Errore nel caricare dati per il form", err);
      setError("Non è stato possibile caricare le opzioni per il form.");
    });
  }, []);

  useEffect(() => {
    if (!formData.accessCode) {
      setCodeIsValid(null);
      setCodeError('');
      return;
    }

    const handler = setTimeout(() => {
      setIsVerifying(true);
      setCodeIsValid(null);
      setCodeError('');
      axios.post('/api/auth/validate-code', { code: formData.accessCode })
        .then(res => {
          if (res.data.isValid) {
            setCodeIsValid(true);
          } else {
            setCodeIsValid(false);
            setCodeError(res.data.message);
          }
        })
        .catch(() => {
          setCodeIsValid(false);
          setCodeError('Errore di connessione durante la verifica.');
        })
        .finally(() => setIsVerifying(false));
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [formData.accessCode]);

  const handleNext = () => {
    if (activeStep === 0 && codeIsValid !== true) {
      setCodeError('È richiesto un codice di accesso valido per procedere. Per assistenza contattare p.codazzi@fondazioneminoprio.it');
      setCodeIsValid(false);
      return;
    }
    setError('');
    setCodeError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSubmit = async () => {
    setError('');
    if (codeIsValid !== true) {
        setError("Il codice di accesso non è valido.");
        return;
    }
    if (!formData.acceptedTerms) {
        setError("Devi accettare i Termini di Servizio e la Privacy Policy.");
        return;
    }
    
    setIsSubmitting(true); // Attiviamo l'animazione di caricamento
    
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      const value = formData[key];
      if ((key === 'categories' || key === 'courses') && Array.isArray(value)) {
        if (value.length > 0) {
            formDataToSend.append(key, JSON.stringify(value.map(item => item.id)));
        }
      } else if (key === 'resume' && value instanceof File) {
        formDataToSend.append(key, value);
      } else if (value !== null && value !== undefined) {
        formDataToSend.append(key, value);
      }
    });
    
    if (!formDataToSend.has('password')) {
        formDataToSend.append('password', formData.password);
    }
    
    try {
      await axios.post('/api/auth/signup/user', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onRegisterSuccess && onRegisterSuccess({ type: 'student' });
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Si è verificato un errore imprevisto. Riprova.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false); // Disattiviamo l'animazione, sia in caso di successo che di errore
    }
  };

  const renderCurrentStepFields = () => {
    const fieldsForThisStep = dynamicFormFields.filter(field => field.step === activeStep);
    return (
      <GenericForm
        fields={fieldsForThisStep}
        formData={formData}
        setFormData={setFormData}
      />
    );
  };

  return (
    <Box component="form" noValidate onSubmit={(e) => e.preventDefault()}>
      <Typography variant="h6" sx={{ mb: 2 }}>Registrazione Studente</Typography>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
      </Stepper>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Box sx={{ mt: 3, minHeight: 250 }}>
        {activeStep === 0 && (
          <>
            <GenericForm
              fields={dynamicFormFields.filter(field => field.step === 0)}
              formData={formData}
              setFormData={setFormData}
            />
            <Tooltip title={codeError} open={codeIsValid === false && !!codeError} placement="bottom-start" arrow>
              <TextField
                margin="normal"
                name="accessCode"
                label="Codice di Accesso"
                value={formData.accessCode}
                onChange={(e) => setFormData(prev => ({ ...prev, accessCode: e.target.value }))}
                fullWidth
                required
                error={codeIsValid === false}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {isVerifying && <CircularProgress size={24} />}
                      {codeIsValid === true && <CheckCircleIcon color="success" />}
                      {codeIsValid === false && !isVerifying && <ErrorIcon color="error" />}
                    </InputAdornment>
                  ),
                }}
              />
            </Tooltip>
            <TextField
              margin="normal"
              name="password"
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              fullWidth
              required
            />
          </>
        )}
        {activeStep > 0 && (
          <>
            {renderCurrentStepFields()}
            {activeStep === steps.length - 1 && (
              <FormControlLabel
                sx={{ mt: 3 }}
                control={
                  <Checkbox
                    checked={formData.acceptedTerms}
                    onChange={(e) => setFormData(prev => ({ ...prev, acceptedTerms: e.target.checked }))}
                  />
                }
                label={
                  <Typography variant="body2">
                    Accetto i{' '}
                    <Link href="/privacy-terms" target="_blank" rel="noopener noreferrer" sx={{ color: '#417a2a' }}>
                      Termini di Servizio e Privacy Policy
                    </Link>
                  </Typography>
                }
              />
            )}
          </>
        )}
      </Box>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>Indietro</Button>
        {activeStep === steps.length - 1 ? (
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={isSubmitting} // Il pulsante è disabilitato durante l'invio
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Completa Registrazione'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNext}>Avanti</Button>
        )}
      </Box>
    </Box>
  );
};